import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { PromptContext, PromptOptions } from '../../contexts/promptContext';

export const PromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<PromptOptions | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [resolver, setResolver] = useState<((value: string | null) => void) | null>(null);

  const prompt = useCallback((opts: PromptOptions) => {
    setOptions({
      cancelText: 'Cancelar',
      confirmText: 'Aceptar',
      title: 'Ingresar valor',
      required: true,
      multiline: false,
      ...opts,
    });
    setInputValue(opts.defaultValue ?? '');
    return new Promise<string | null>((resolve) => {
      setResolver(() => resolve);
    });
  }, []);

  useEffect(() => {
    if (!options) setInputValue('');
  }, [options]);

  const close = (result: string | null) => {
    if (resolver) resolver(result);
    setResolver(null);
    setOptions(null);
    setInputValue('');
  };

  const handleConfirm = () => {
    const trimmed = inputValue.trim();
    if (options?.required !== false && !trimmed) return;
    close(options?.required === false ? inputValue : trimmed);
  };

  const canConfirm = options?.required === false || inputValue.trim().length > 0;

  const contextValue = useMemo(() => ({ prompt }), [prompt]);

  const inputClass =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30';

  return (
    <PromptContext.Provider value={contextValue}>
      {children}
      <Dialog open={Boolean(options)} onOpenChange={(open) => { if (!open) close(null); }}>
        {options && (
        <DialogContent>
          <DialogHeader>
            {options.title && <DialogTitle>{options.title}</DialogTitle>}
            {options.message && <DialogDescription>{options.message}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-2 py-1">
            {options.label && (
              <label className="block text-sm font-medium text-foreground">{options.label}</label>
            )}
            {options.multiline ? (
              <textarea
                className={`${inputClass} min-h-[100px] resize-y`}
                value={inputValue}
                placeholder={options.placeholder}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
              />
            ) : (
              <input
                type="text"
                className={inputClass}
                value={inputValue}
                placeholder={options.placeholder}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canConfirm) {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
                autoFocus
              />
            )}
          </div>
          <DialogFooter>
            <button
              type="button"
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium px-6 py-2 rounded-lg transition-all duration-200"
              onClick={() => close(null)}
            >
              {options.cancelText}
            </button>
            <button
              type="button"
              disabled={!canConfirm}
              className="bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white font-medium px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none"
              onClick={handleConfirm}
            >
              {options.confirmText}
            </button>
          </DialogFooter>
        </DialogContent>
        )}
      </Dialog>
    </PromptContext.Provider>
  );
};
